import { t } from "@lingui/macro";
import { useAtom } from "jotai";
import { lazy, ReactNode, Suspense, useMemo } from "react";
import { matchPath, Navigate, useLocation } from "react-router-dom";
import { shouldDisableNFTRoutesAtom } from "state/application/atoms";
import styled from "styled-components";
import { LoadingBubble } from "components/Tokens/loading";
import { isBrowserRouterEnabled } from "utils/env";

// High-traffic pages (index and /swap) should not be lazy-loaded.
import Landing from "./Landing";
import Swap from "./Swap";

const NftExplore = lazy(() => import("nft/pages/explore"));
const Collection = lazy(() => import("nft/pages/collection"));
const Profile = lazy(() => import("nft/pages/profile"));
const Asset = lazy(() => import("nft/pages/asset/Asset"));
const AddLiquidityWithTokenRedirects = lazy(
  () => import("pages/AddLiquidity/redirects")
);
const AddLiquidityV2WithTokenRedirects = lazy(
  () => import("pages/AddLiquidityV2/redirects")
);
const RedirectExplore = lazy(() => import("pages/Explore/redirects"));
const GlueExplore = lazy(() => import("pages/Explore/GlueExplore"));
const GluePoolDetails = lazy(() => import("pages/Explore/GluePoolDetails"));
const MigrateV2 = lazy(() => import("pages/MigrateV2"));
const MigrateV2Pair = lazy(() => import("pages/MigrateV2/MigrateV2Pair"));
const NotFound = lazy(() => import("pages/NotFound"));
const Pool = lazy(() => import("pages/Pool"));
const PositionPage = lazy(() => import("pages/Pool/PositionPage"));
const PoolV2 = lazy(() => import("pages/Pool/v2"));
const PoolDetails = lazy(() => import("pages/PoolDetails"));
const PoolFinder = lazy(() => import("pages/PoolFinder"));
const RemoveLiquidity = lazy(() => import("pages/RemoveLiquidity"));
const RemoveLiquidityV3 = lazy(() => import("pages/RemoveLiquidity/V3"));
const TokenDetails = lazy(() => import("pages/TokenDetails"));
const Vote = lazy(() => import("pages/Vote"));

const LazyLoadContainer = styled.div`
  padding: 48px 40px 0px;
  max-width: 1280px;
  margin: 0 auto;
  width: 100%;
  min-width: 320px;

  @media only screen and (max-width: ${({ theme }) =>
      `${theme.breakpoint.sm}px`}) {
    padding: 16px;
    padding-bottom: 0px;
  }
`;

const LoadingStatsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 40px;

  @media only screen and (max-width: ${({ theme }) =>
      `${theme.breakpoint.sm}px`}) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-bottom: 24px;
  }
`;

const LoadingStatCard = styled.div`
  background: ${({ theme }) => theme.surface2};
  border-radius: 20px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid ${({ theme }) => theme.surface3};

  @media only screen and (max-width: ${({ theme }) =>
      `${theme.breakpoint.sm}px`}) {
    padding: 20px;
  }
`;

const LoadingTableContainer = styled.div`
  background: ${({ theme }) => theme.surface1};
  border-radius: 20px;
  overflow: hidden;
`;

const LoadingTableHeader = styled.div`
  padding: 24px;
`;

const LazyLoadSpinner = () => (
  <LazyLoadContainer>
    {/* Loading statistics cards */}
    <LoadingStatsContainer>
      <LoadingStatCard>
        <LoadingBubble height="20px" width="140px" margin="0 0 8px 0" />
        <LoadingBubble height="32px" width="120px" />
      </LoadingStatCard>
      <LoadingStatCard>
        <LoadingBubble height="20px" width="120px" margin="0 0 8px 0" />
        <LoadingBubble height="32px" width="110px" />
      </LoadingStatCard>
      <LoadingStatCard>
        <LoadingBubble height="20px" width="100px" margin="0 0 8px 0" />
        <LoadingBubble height="32px" width="90px" />
      </LoadingStatCard>
    </LoadingStatsContainer>

    {/* Loading table */}
    <LoadingTableContainer>
      <LoadingTableHeader>
        <LoadingBubble height="28px" width="200px" margin="0 0 8px 0" />
        <LoadingBubble height="16px" width="280px" />
      </LoadingTableHeader>
      <div style={{ padding: "24px" }}>
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              gap: "16px",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <LoadingBubble height="40px" width="40px" round />
            <LoadingBubble height="20px" width="120px" />
            <LoadingBubble height="20px" width="80px" />
            <LoadingBubble height="20px" width="100px" />
            <LoadingBubble height="20px" width="90px" />
          </div>
        ))}
      </div>
    </LoadingTableContainer>
  </LazyLoadContainer>
);

interface RouterConfig {
  browserRouterEnabled?: boolean;
  hash?: string;
  shouldDisableNFTRoutes?: boolean;
}

/**
 * Convenience hook which organizes the router configuration into a single object.
 */
export function useRouterConfig(): RouterConfig {
  const browserRouterEnabled = isBrowserRouterEnabled();
  const { hash } = useLocation();
  const [shouldDisableNFTRoutes] = useAtom(shouldDisableNFTRoutesAtom);
  return useMemo(
    () => ({
      browserRouterEnabled,
      hash,
      shouldDisableNFTRoutes: Boolean(shouldDisableNFTRoutes),
    }),
    [browserRouterEnabled, hash, shouldDisableNFTRoutes]
  );
}

export interface RouteDefinition {
  path: string;
  nestedPaths: string[];
  getTitle: (path?: string) => string;
  enabled: (args: RouterConfig) => boolean;
  getElement: (args: RouterConfig) => ReactNode;
}

// Assigns the defaults to the route definition.
function createRouteDefinition(
  route: Partial<RouteDefinition>
): RouteDefinition {
  return {
    getElement: () => null,
    getTitle: () => "GlueSwap",
    enabled: () => true,
    path: "/",
    nestedPaths: [],
    // overwrite the defaults
    ...route,
  };
}

const SwapTitle = t`Buy, sell & trade Ethereum and other top tokens on GlueSwap`;

export const routes: RouteDefinition[] = [
  createRouteDefinition({
    path: "/",
    getTitle: () =>
      t`GlueSwap | Trade crypto & NFTs safely on the top DeFi exchange`,
    getElement: (args) => {
      return args.browserRouterEnabled && args.hash ? (
        <Navigate to={args.hash.replace("#", "")} replace />
      ) : (
        <Landing />
      );
    },
  }),
  createRouteDefinition({
    path: "/explore",
    getTitle: () => t`Explore pools and analytics on GlueSwap`,
    getElement: () => (
      <Suspense fallback={<LazyLoadSpinner />}>
        <GlueExplore />
      </Suspense>
    ),
  }),
  // createRouteDefinition({
  //   path: '/explore/tokens/:chainName/:tokenAddress',
  //   getTitle: () => t`Buy & sell on GlueSwap`,
  //   getElement: () => <TokenDetails />,
  // }),
  // createRouteDefinition({
  //   path: '/tokens',
  //   getTitle: getDefaultTokensTitle,
  //   getElement: () => <Navigate to="/explore/tokens" replace />,
  // }),
  // createRouteDefinition({
  //   path: '/tokens/:chainName',
  //   getTitle: getDefaultTokensTitle,
  //   getElement: () => <RedirectExplore />,
  // }),
  // createRouteDefinition({
  //   path: '/tokens/:chainName/:tokenAddress',
  //   getTitle: getDefaultTokensTitle,
  //   getElement: () => <RedirectExplore />,
  // }),
  createRouteDefinition({
    path: "/explore/pools/:chainName/:poolAddress",
    getTitle: () => t`Explore pools on GlueSwap`,
    getElement: () => {
      return (
        <Suspense fallback={<LazyLoadSpinner />}>
          <GluePoolDetails />
        </Suspense>
      );
    },
  }),
  // createRouteDefinition({
  //   path: '/vote/*',
  //   getTitle: () => t`Vote on governance proposals on GlueSwap`,
  //   getElement: () => (
  //     <Suspense fallback={<LazyLoadSpinner />}>
  //       <Vote />
  //     </Suspense>
  //   ),
  // }),
  // createRouteDefinition({
  //   path: '/create-proposal',
  //   getTitle: () => t`Create a new governance proposal on GlueSwap`,
  //   getElement: () => <Navigate to="/vote/create-proposal" replace />,
  // }),
  createRouteDefinition({
    path: "/send",
    getElement: () => <Swap />,
    getTitle: () => t`Send tokens on GlueSwap`,
  }),
  // createRouteDefinition({
  //   path: '/limits',
  //   getElement: () => <Navigate to="/limit" replace />,
  // }),
  // createRouteDefinition({
  //   path: '/limit',
  //   getElement: () => <Swap />,
  //   getTitle: () => SwapTitle,
  // }),
  createRouteDefinition({
    path: "/swap",
    getElement: () => <Swap />,
    getTitle: () => SwapTitle,
  }),
  // createRouteDefinition({
  //   path: '/pool/v2/find',
  //   getElement: () => <PoolFinder />,
  //   getTitle: () => t`Explore top liquidity pools (v2) on GlueSwap`,
  // }),
  // createRouteDefinition({
  //   path: '/pool/v2',
  //   getElement: () => <PoolV2 />,
  //   getTitle: () => t`Provide liquidity to pools (v2) on GlueSwap`,
  // }),
  createRouteDefinition({
    path: "/pool",
    getElement: () => <Pool />,
    getTitle: () => t`Manage & provide pool liquidity on GlueSwap`,
  }),
  createRouteDefinition({
    path: "/pool/:tokenId",
    getElement: () => <PositionPage />,
    getTitle: () => t`Manage pool liquidity on GlueSwap`,
  }),
  // createRouteDefinition({
  //   path: '/pools/v2/find',
  //   getElement: () => <PoolFinder />,
  //   getTitle: () => t`Explore top liquidity pools (v2) on GlueSwap`,
  // }),
  // createRouteDefinition({
  //   path: '/pools/v2',
  //   getElement: () => <PoolV2 />,
  //   getTitle: () => t`Manage & provide v2 pool liquidity on GlueSwap`,
  // }),
  createRouteDefinition({
    path: "/pools",
    getElement: () => <Pool />,
    getTitle: () => t`Manage & provide pool liquidity on GlueSwap`,
  }),
  createRouteDefinition({
    path: "/pools/:tokenId",
    getElement: () => <PositionPage />,
    getTitle: () => t`Manage pool liquidity on GlueSwap`,
  }),
  // createRouteDefinition({
  //   path: '/add/v2',
  //   nestedPaths: [':currencyIdA', ':currencyIdA/:currencyIdB'],
  //   getElement: () => <AddLiquidityV2WithTokenRedirects />,
  //   getTitle: () => t`Provide liquidity to pools (v2) on GlueSwap`,
  // }),
  createRouteDefinition({
    path: "/add",
    nestedPaths: [
      ":currencyIdA",
      ":currencyIdA/:currencyIdB",
      ":currencyIdA/:currencyIdB/:feeAmount",
      ":currencyIdA/:currencyIdB/:feeAmount/:tokenId",
    ],
    getElement: () => <AddLiquidityWithTokenRedirects />,
    getTitle: () => t`Provide liquidity to pools on GlueSwap`,
  }),
  // createRouteDefinition({
  //   path: '/remove/v2/:currencyIdA/:currencyIdB',
  //   getElement: () => <RemoveLiquidity />,
  //   getTitle: () => t`Manage v2 pool liquidity on GlueSwap`,
  // }),
  createRouteDefinition({
    path: "/remove/:tokenId",
    getElement: () => <RemoveLiquidityV3 />,
    getTitle: () => t`Manage pool liquidity on GlueSwap`,
  }),
  createRouteDefinition({
    path: "/migrate/v2",
    getElement: () => <MigrateV2 />,
    getTitle: () => t`Migrate v2 pool liquidity to GlueSwap v3`,
  }),
  // createRouteDefinition({
  //   path: '/migrate/v2/:address',
  //   getElement: () => <MigrateV2Pair />,
  //   getTitle: () => t`Migrate v2 pool liquidity to GlueSwap v3`,
  // }),
  // createRouteDefinition({
  //   path: '/nfts',
  //   getElement: () => (
  //     <Suspense fallback={null}>
  //       <NftExplore />
  //     </Suspense>
  //   ),
  //   enabled: (args) => !args.shouldDisableNFTRoutes,
  //   getTitle: () => t`Trade NFTs across OpenSea & other top marketplaces on GlueSwap`,
  // }),
  // createRouteDefinition({
  //   path: '/nfts/asset/:contractAddress/:tokenId',
  //   getElement: () => (
  //     <Suspense fallback={null}>
  //       <Asset />
  //     </Suspense>
  //   ),
  //   enabled: (args) => !args.shouldDisableNFTRoutes,
  //   getTitle: () => t`Explore NFTs on GlueSwap`,
  // }),
  // createRouteDefinition({
  //   path: '/nfts/profile',
  //   getElement: () => (
  //     <Suspense fallback={null}>
  //       <Profile />
  //     </Suspense>
  //   ),
  //   enabled: (args) => !args.shouldDisableNFTRoutes,
  //   getTitle: () => t`Explore NFTs on GlueSwap`,
  // }),
  // createRouteDefinition({
  //   path: '/nfts/collection/:contractAddress',
  //   getElement: () => (
  //     <Suspense fallback={null}>
  //       <Collection />
  //     </Suspense>
  //   ),
  //   enabled: (args) => !args.shouldDisableNFTRoutes,
  //   getTitle: () => t`Explore NFTs on GlueSwap`,
  // }),
  // createRouteDefinition({
  //   path: '/nfts/collection/:contractAddress/activity',
  //   getElement: () => (
  //     <Suspense fallback={null}>
  //       <Collection />
  //     </Suspense>
  //   ),
  //   enabled: (args) => !args.shouldDisableNFTRoutes,
  //   getTitle: () => t`Explore NFTs on GlueSwap`,
  // }),
  createRouteDefinition({
    path: "*",
    getElement: () => <Navigate to="/not-found" replace />,
  }),
  createRouteDefinition({ path: "/not-found", getElement: () => <NotFound /> }),
];

export const findRouteByPath = (pathname: string) => {
  for (const route of routes) {
    const match = matchPath(route.path, pathname);
    if (match) {
      return route;
    }
    const subPaths = route.nestedPaths.map(
      (nestedPath) => `${route.path}/${nestedPath}`
    );
    for (const subPath of subPaths) {
      const match = matchPath(subPath, pathname);
      if (match) {
        return route;
      }
    }
  }
  return undefined;
};
